import { motion } from 'framer-motion';
import { Mail, Phone, Linkedin, Target, Heart, Users, Handshake, ArrowUpRight, Sparkles } from 'lucide-react';
import { FallingMapleLeaves } from '@/components/shared/FallingMapleLeaves';

const team = [
  {
    name: 'Tejas Patil',
    role: 'Co-Founder',
    expertise: 'Finance, Climate Finance, ESG',
    email: 'tejaspatil@mapleintegratedsolutions.com',
    phone: '+91 77384 81009',
    linkedin: 'http://linkedin.com/in/tejaspatilin',
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_4d73cdde-a968-4e98-9845-6bbefee99d6f/artifacts/pkfethmg_IMG_9511.webp',
    imagePosition: 'object-top'
  },
  {
    name: 'Vidhi Gaur',
    role: 'Advisor, Maple',
    expertise: 'DMRV, Carbon Markets',
    email: 'vidhigaur@mapleintegratedsolutions.com',
    phone: '+91 93062 05810',
    linkedin: 'http://linkedin.com/in/vidhigaur1',
    image: 'https://customer-assets.emergentagent.com/job_1961d3d3-b772-4517-a984-1bde18f8505c/artifacts/agpciew1_Untitled%20design%20%284%29.png',
    imagePosition: 'object-center'
  },
  {
    name: 'Anshdha Nandra',
    role: 'Consultant',
    expertise: 'ESG Advisory, Sustainability Reporting',
    email: 'anshdhanandra@mapleintegratedsolutions.com',
    phone: '+91 88269 79259',
    linkedin: 'http://linkedin.com/in/anshdha-nandra-07a62620a',
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_4d73cdde-a968-4e98-9845-6bbefee99d6f/artifacts/q3t5h59r_WhatsApp%20Image%202026-07-24%20at%2012.03.16.jpeg',
    imagePosition: 'object-top'
  }
];

const ethos = [
  {
    icon: Target,
    title: 'Customised Solutions',
    description: 'Crafting sustainability strategies that fit businesses, not force them.',
    accent: 'from-brand-green/20 to-brand-green/5'
  },
  {
    icon: Heart,
    title: 'Credibility & Expertise',
    description: 'Delivering authentic ESG knowledge through skilled professionals passionate about impact.',
    accent: 'from-brand-brown/20 to-brand-brown/5'
  },
  {
    icon: Users,
    title: 'Support for MSMEs',
    description: 'Making sustainability accessible for small businesses to overcome barriers and thrive.',
    accent: 'from-brand-green/20 to-brand-green/5'
  },
  {
    icon: Handshake,
    title: 'Collaboration & Partnership',
    description: 'Working hand-in-hand with businesses, stakeholders, and communities to drive collective progress.',
    accent: 'from-brand-brown/20 to-brand-brown/5'
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const floatVariants = {
  initial: { y: 0 },
  animate: { 
    y: [-8, 8, -8],
    transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' }
  }
};

const AboutPage = () => {
  return (
    <main data-testid="about-page" className="overflow-hidden">
      {/* Hero Section - Enhanced */}
      <section className="relative min-h-[60vh] flex items-center pt-24 pb-12">
        {/* Artistic Background with layered effects */}
        <div className="absolute inset-0 -top-20">
          <img 
            src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1920&q=80"
            alt="Forest sunlight"
            className="w-full h-full object-cover"
            style={{ filter: 'saturate(0.6) brightness(1.1) contrast(0.95) sepia(0.15)' }}
          />
          {/* Enhanced gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white"></div>
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-green/8 via-transparent to-brand-brown/12"></div>
        </div>

        <FallingMapleLeaves />

        {/* Floating decorative elements */}
        <motion.div 
          variants={floatVariants}
          initial="initial"
          animate="animate"
          className="absolute right-[15%] top-[20%] w-20 h-20 rounded-full bg-gradient-to-br from-brand-green/20 to-brand-green/5 blur-xl hidden lg:block"
        />
        <motion.div 
          variants={floatVariants}
          initial="initial"
          animate="animate"
          style={{ animationDelay: '2s' }}
          className="absolute right-[25%] bottom-[25%] w-32 h-32 rounded-full bg-gradient-to-br from-brand-brown/15 to-brand-brown/5 blur-xl hidden lg:block"
        />
        
        <div className="w-full pl-6 md:pl-12 lg:pl-24 pr-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="max-w-2xl"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-green/20 mb-6"
            >
              <Sparkles className="w-4 h-4 text-brand-green" />
              <span className="font-sans text-sm font-semibold text-brand-green uppercase tracking-wider">
                About Us
              </span>
            </motion.div>
            
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-dark mb-8 leading-[1.1]">
              Our <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Purpose</span>
            </h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="font-sans text-lg md:text-xl text-gray-700 leading-relaxed max-w-xl"
            >
              To make sustainability adaptable, structured around each organization's disclosure requirements, strategic priorities, and on-ground operations.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="mt-10 flex items-center gap-4"
            >
              <div className="h-px w-12 bg-gradient-to-r from-brand-green to-brand-brown"></div>
              <span className="font-sans text-sm text-gray-500 italic">Driving sustainable transformation</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Ethos Section - Redesigned with creative layout */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white via-brand-surface/50 to-brand-surface relative">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233ba236' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-12"
          >
            <span className="inline-block font-sans text-sm font-semibold text-brand-brown uppercase tracking-wider px-4 py-1.5 rounded-full bg-brand-brown/10 mb-4">
              Our Values
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-dark">
              The Maple <span className="italic text-brand-green">Ethos</span>
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8"
          >
            {ethos.map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -4, transition: { duration: 0.3 } }}
                className="group relative"
                data-testid={`ethos-card-${index}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.accent} rounded-3xl transform group-hover:scale-105 transition-transform duration-500 opacity-0 group-hover:opacity-100`}></div>
                <div className="relative bg-white p-8 md:p-10 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-transparent transition-all duration-500">
                  <div className="flex items-start gap-6">
                    <div className={`flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br ${index % 2 === 0 ? 'from-brand-green/15 to-brand-green/5' : 'from-brand-brown/15 to-brand-brown/5'} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <item.icon className={`w-8 h-8 ${index % 2 === 0 ? 'text-brand-green' : 'text-brand-brown'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-xl text-brand-dark mb-3 group-hover:text-brand-green transition-colors duration-300">{item.title}</h3>
                      <p className="font-sans text-gray-600 leading-relaxed text-base">{item.description}</p>
                    </div>
                  </div>
                  <div className={`absolute bottom-4 right-4 w-8 h-8 rounded-full ${index % 2 === 0 ? 'bg-brand-green/10' : 'bg-brand-brown/10'} flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
                    <ArrowUpRight className={`w-4 h-4 ${index % 2 === 0 ? 'text-brand-green' : 'text-brand-brown'}`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team Section - Premium redesign */}
      <section className="py-16 md:py-20 bg-white relative overflow-hidden" data-testid="team-section">
        {/* Subtle background gradient */}
        <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-brand-surface/30 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-12"
          >
            <span className="inline-block font-sans text-sm font-semibold text-brand-green uppercase tracking-wider px-4 py-1.5 rounded-full bg-brand-green/10 mb-4">
              Our Team
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-dark mb-4">
              Meet the <span className="italic bg-gradient-to-r from-brand-green to-brand-brown bg-clip-text text-transparent">Experts</span>
            </h2>
            <p className="font-sans text-gray-600 max-w-2xl mx-auto text-lg">
              Passionate professionals committed to driving sustainable transformation
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10"
          >
            {team.map((member, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -8 }}
                className="group"
                data-testid={`team-member-${index}`}
              >
                <div className="bg-white rounded-3xl overflow-hidden shadow-lg shadow-gray-200/50 hover:shadow-2xl hover:shadow-brand-green/10 transition-all duration-500 border border-gray-100 hover:border-brand-green/20">
                  {/* Image container with overlay */}
                  <div className="relative h-80 overflow-hidden">
                    <img 
                      src={member.image}
                      alt={member.name}
                      className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 ${member.imagePosition || 'object-center'}`}
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Social link on hover */}
                    <motion.a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300 hover:bg-brand-green hover:text-white"
                      whileHover={{ scale: 1.1 }}
                    >
                      <Linkedin className="w-5 h-5" />
                    </motion.a>
                  </div>
                  
                  {/* Content */}
                  <div className="p-7">
                    <div className="mb-4">
                      <h3 className="font-serif text-2xl text-brand-dark mb-1 group-hover:text-brand-green transition-colors duration-300">{member.name}</h3>
                      <p className="font-sans text-sm font-semibold text-brand-brown">{member.role}</p>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-5">
                      {member.expertise.split(', ').map((skill, skillIndex) => (
                        <span 
                          key={skillIndex}
                          className="px-3 py-1 text-xs font-sans bg-brand-surface rounded-full text-gray-600"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                    
                    <div className="space-y-3 pt-4 border-t border-gray-100">
                      <a 
                        href={`mailto:${member.email}`}
                        className="flex items-center gap-3 text-gray-600 hover:text-brand-green transition-colors group/link"
                      >
                        <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center group-hover/link:bg-brand-green group-hover/link:text-white transition-all">
                          <Mail className="w-4 h-4" />
                        </div>
                        <span className="font-sans text-xs truncate">{member.email}</span>
                      </a>
                      <a 
                        href={`tel:${member.phone.replace(/\s/g, '')}`}
                        className="flex items-center gap-3 text-gray-600 hover:text-brand-green transition-colors group/link"
                      >
                        <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center group-hover/link:bg-brand-green group-hover/link:text-white transition-all">
                          <Phone className="w-4 h-4" />
                        </div>
                        <span className="font-sans text-sm">{member.phone}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
